import { ActionIcon, Avatar, Badge, Collapse, List, Tooltip } from '@mantine/core';
import {
  IconAlertSquareRounded,
  IconBug,
  IconChevronDown,
  IconCircleKeyFilled,
  IconMinus,
  IconPlus,
  IconSparkles,
  IconStrikethrough,
  IconTool,
} from '@tabler/icons-react';
import { clsx } from 'clsx';
import { useAtom, useSetAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { match } from 'ts-pattern';
import { adminUiHiddenAtom, screenshotDiffAtom } from '~/atoms/atoms';
import type { ArrayDiff, DiffType } from '~/common/array/findDiffs';
import IconAIEdit from '~/components/icons/IconAIEdit';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import type { SelectedStep } from '~/server/logic/prisma/step-select';
import { api } from '~/trpc-extra/api';
import type { AiActionLog, AiChange } from '~/types';
import { MarkdownStepDescription } from '~/components/common/MarkdownStepDescription';
import OperationDiff from './OperationDiff';

function StepHeader({
  step,
  type,
  expanded,
  setExpanded,
  aiChanges,
  side,
  testRunAttemptId,
  stepDiff,
}: {
  step: SelectedStep;
  type: DiffType;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  aiChanges?: AiChange[];
  side: 'left' | 'right';
  testRunAttemptId?: string;
  stepDiff: ArrayDiff<SelectedStep>;
}) {
  const stepHealSuccess = aiChanges?.every((c) => c.isSuccess);
  const { isAdmin } = useAuthData();
  const [adminUiHidden] = useAtom(adminUiHiddenAtom);
  const { mutateAsync: getLangSmithAiTraceUrl } = api.admin.getLangSmithAiTraceUrl.useMutation();

  return (
    <div
      className={clsx(
        'relative flex h-full cursor-pointer items-center justify-between px-4 py-2 transition-colors',
        side === 'left' ? 'rounded-tl-lg' : 'rounded-tr-lg',
        'group-hover:bg-[var(--mantine-color-default-hover)]',
        {
          'bg-red-50 dark:bg-red-900/20': type === 'deleted',
          'bg-emerald-50 dark:bg-emerald-900/20': type === 'added',
          'bg-blue-50 dark:bg-blue-900/20': type === 'modified',
          'opacity-50': step.skip,
        },
      )}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex h-full items-center space-x-3">
        {!!step.skip && (
          <Tooltip label="Step was skipped">
            <Avatar
              size="sm"
              radius="xl"
              className="opacity-60 hover:opacity-80"
            >
              <IconStrikethrough size={14} />
            </Avatar>
          </Tooltip>
        )}

        <div className="flex items-center space-x-2">
          {match(step.mode)
            .with('STRICT', () => (
              <IconCircleKeyFilled
                className="flex-shrink-0 text-[var(--mantine-color-dimmed)]"
                size={14}
              />
            ))
            .with('AI_DYNAMIC', () => <IconAIEdit className="h-3.5 flex-shrink-0 text-[var(--mantine-color-dimmed)]" />)
            .otherwise(() => null)}
          <MarkdownStepDescription content={step.description} />
        </div>
      </div>

      <div className="ml-4 flex items-center space-x-3">
        {type !== 'unchanged' && (
          <span
            className={clsx('flex h-5 items-center rounded px-1.5 text-xs font-medium', {
              'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400': type === 'deleted',
              'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400': type === 'added',
              'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400': type === 'modified',
            })}
          >
            {match(type)
              .with('deleted', () => <IconMinus size={14} />)
              .with('added', () => <IconPlus size={14} />)
              .with('modified', () => 'M')
              .exhaustive()}
          </span>
        )}
        {!!aiChanges && aiChanges.length > 0 && (
          <Tooltip label={stepHealSuccess ? 'AI successfully healed this step' : 'AI attempted to heal this step'}>
            <Badge
              variant="outline"
              size="sm"
              color={stepHealSuccess ? 'blue' : 'orange'}
              className={
                stepHealSuccess ?
                  'flex-shrink-0 text-blue-600 dark:text-blue-600'
                : 'flex-shrink-0 text-yellow-600 dark:text-yellow-600'
              }
            >
              <div className="flex flex-row gap-x-2">
                <IconSparkles
                  className={
                    stepHealSuccess ? 'text-blue-600 dark:text-blue-600' : 'text-yellow-600 dark:text-yellow-600'
                  }
                  size={14}
                />
              </div>
            </Badge>
          </Tooltip>
        )}

        {/* Debug button for autoheal steps (admin only) */}
        {!!isAdmin &&
          !adminUiHidden &&
          !!aiChanges &&
          aiChanges.length > 0 &&
          !!testRunAttemptId &&
          side === 'right' &&
          !!stepDiff.prev && (
            <Tooltip label="Debug Autoheal in LangSmith">
              <ActionIcon
                size="xs"
                variant="subtle"
                className="text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    const traceUrl = await getLangSmithAiTraceUrl({
                      executionId: testRunAttemptId!,
                      stepId: stepDiff.prev?.id ?? step.id,
                      initiator: 'executor', // StepDiff is used in test execution context
                    });
                    window.open(traceUrl, '_blank', 'noopener,noreferrer');
                  } catch (error) {
                    console.error('Failed to get LangSmith autoheal trace URL:', error);
                  }
                }}
              >
                <IconBug size={14} />
              </ActionIcon>
            </Tooltip>
          )}

        {/* Debug button for AI steps (admin only) */}
        {!!isAdmin && !adminUiHidden && step.mode === 'AI_DYNAMIC' && !!testRunAttemptId && side === 'right' && (
          <Tooltip label="Debug AI Step in LangSmith">
            <ActionIcon
              size="xs"
              variant="subtle"
              className="text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20"
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  const traceUrl = await getLangSmithAiTraceUrl({
                    executionId: testRunAttemptId!,
                    stepId: stepDiff.prev?.id ?? step.id,
                    initiator: 'executor', // StepDiff is used in test execution context
                  });
                  window.open(traceUrl, '_blank', 'noopener,noreferrer');
                } catch (error) {
                  console.error('Failed to get LangSmith AI step trace URL:', error);
                }
              }}
            >
              <IconBug size={14} />
            </ActionIcon>
          </Tooltip>
        )}

        <ActionIcon
          size="xs"
          variant="transparent"
          color="gray"
        >
          <IconChevronDown
            className={clsx('transition-transform', {
              'rotate-180': expanded,
            })}
            size={14}
          />
        </ActionIcon>
      </div>
    </div>
  );
}

export default function StepDiff({
  stepDiff,
  stepIndex,
  operationDiffs,
  aiChanges,
  allExpanded,
  testRunAttemptId,
  aiActionLogs = [],
}: {
  stepDiff: ArrayDiff<SelectedStep>;
  stepIndex: number;
  operationDiffs: ArrayDiff<SelectedOperation>[];
  aiChanges?: AiChange[];
  allExpanded?: boolean;
  testRunAttemptId?: string;
  aiActionLogs?: AiActionLog[];
}) {
  const setScreenshotDiff = useSetAtom(screenshotDiffAtom);

  const hasOperationChanges = useMemo(
    () => operationDiffs.some((opDiff) => opDiff.type !== 'unchanged'),
    [operationDiffs.length],
  );

  const [expanded, setExpanded] = useState(hasOperationChanges);

  useEffect(() => {
    // undefined means the user has not interacted with the expand/collapse all button
    if (allExpanded !== undefined) {
      setExpanded(allExpanded);
    }
  }, [allExpanded]);

  const screenshotOperationIdPairs = useMemo(() => {
    return operationDiffs.reduce<[string | undefined, string | undefined][]>((acc, diff) => {
      const prevId = diff.prev?.id ?? acc.at(-1)?.at(0);
      const newId = diff.new?.id ?? acc.at(-1)?.at(1);
      acc.push([prevId, newId]);
      return acc;
    }, []);
  }, [operationDiffs]);

  const stepHealSuccess = aiChanges?.every((c) => c.isSuccess);

  return (
    <div className="flex items-start">
      <div className="mr-4 w-3 pt-[10px] text-sm text-[var(--mantine-color-dimmed)]">{stepIndex + 1}</div>
      <div className="flex-1">
        {/* Entire Step Container */}
        <div className="overflow-hidden rounded-lg border border-[var(--mantine-color-default-border)] shadow-sm transition-colors hover:border-neutral-400 dark:hover:border-neutral-600">
          {/* Two Side-by-Side Headers */}
          <div className="table w-full">
            <div className="group table-row">
              {/* Previous Step Header */}
              <div className="table-cell w-1/2 border-r border-[var(--mantine-color-default-border)] align-top">
                {!!stepDiff.prev && (
                  <StepHeader
                    step={stepDiff.prev}
                    type={stepDiff.type === 'modified' || hasOperationChanges ? 'deleted' : stepDiff.type}
                    expanded={expanded}
                    setExpanded={() => setExpanded(!expanded)}
                    side="left"
                    testRunAttemptId={testRunAttemptId}
                    stepDiff={stepDiff}
                  />
                )}
              </div>

              {/* Current Step Header */}
              <div className="table-cell w-1/2">
                {!!stepDiff.new && (
                  <StepHeader
                    step={stepDiff.new}
                    type={stepDiff.type === 'modified' || hasOperationChanges ? 'added' : stepDiff.type}
                    expanded={expanded}
                    setExpanded={() => setExpanded(!expanded)}
                    aiChanges={aiChanges}
                    side="right"
                    testRunAttemptId={testRunAttemptId}
                    stepDiff={stepDiff}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Operations Table */}
          <Collapse in={expanded}>
            <div className="border-t border-[var(--mantine-color-default-border)]">
              {/* AI Dynamic Step Notice */}
              {(stepDiff.prev?.mode === 'AI_DYNAMIC' || stepDiff.new?.mode === 'AI_DYNAMIC') && (
                <div className="table w-full">
                  <div className="table-row">
                    <div className="table-cell w-1/2 border-r border-[var(--mantine-color-default-border)] p-4">
                      {stepDiff.prev?.mode === 'AI_DYNAMIC' && (
                        <div className="space-y-2">
                          <div className="rounded py-2 pl-4 text-xs font-medium text-[var(--mantine-color-dimmed)]">
                            AI step - dynamic actions each time
                          </div>
                          <div className="rounded-md border border-orange-200 bg-orange-50 p-3 dark:border-orange-800 dark:bg-orange-900/20">
                            <div className="flex items-start space-x-2">
                              <IconAlertSquareRounded
                                size={16}
                                className="mt-0.5 flex-shrink-0 text-orange-600 dark:text-orange-400"
                              />
                              <div className="text-xs">
                                <div className="mb-1 font-medium text-orange-800 dark:text-orange-200">Deprecated</div>
                                <div className="text-orange-700 dark:text-orange-300">
                                  If this step is truly dynamic, migrate to an <strong>&ldquo;AI Act&rdquo;</strong>{' '}
                                  action. Search for &ldquo;AI Act&rdquo; in the editor.
                                  <br />
                                  <br />
                                  If the content is not dynamic, migrate to <strong>
                                    &ldquo;Auto-heal&rdquo;
                                  </strong>{' '}
                                  step mode by clicking the <strong>Save</strong> button for this step when editing the
                                  test.
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="table-cell w-1/2 p-4">
                      {stepDiff.new?.mode === 'AI_DYNAMIC' && (
                        <div className="space-y-2">
                          <div className="rounded py-2 pl-4 text-xs font-medium text-[var(--mantine-color-dimmed)]">
                            AI step - dynamic actions each time
                          </div>
                          <div className="rounded-md border border-orange-200 bg-orange-50 p-3 dark:border-orange-800 dark:bg-orange-900/20">
                            <div className="flex items-start space-x-2">
                              <IconAlertSquareRounded
                                size={16}
                                className="mt-0.5 flex-shrink-0 text-orange-600 dark:text-orange-400"
                              />
                              <div className="text-xs">
                                <div className="mb-1 font-medium text-orange-800 dark:text-orange-200">Deprecated</div>
                                <div className="text-orange-700 dark:text-orange-300">
                                  If this step is truly dynamic, migrate to an <strong>&ldquo;AI Act&rdquo;</strong>{' '}
                                  action. Search for &ldquo;AI Act&rdquo; in the editor.
                                  <br />
                                  <br />
                                  If the content is not dynamic, migrate to <strong>
                                    &ldquo;Auto-heal&rdquo;
                                  </strong>{' '}
                                  step mode by clicking the <strong>Save</strong> button for this step when editing the
                                  test.
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Operations Content using display table */}
              {operationDiffs.length > 0 && (
                <div className="table w-full">
                  {operationDiffs.map((diff, idx) => (
                    <div
                      key={`${diff.new?.id}_${diff.prev?.id}`}
                      className="table-row"
                    >
                      {/* Previous Operation */}
                      <div
                        className={clsx(
                          'table-cell w-1/2 border-r border-[var(--mantine-color-default-border)] px-4 py-1 align-top',
                          {
                            'pt-4': idx === 0,
                            'pb-4': idx === operationDiffs.length - 1 && !aiChanges?.length,
                          },
                        )}
                      >
                        {diff.prev ?
                          <OperationDiff
                            step={stepDiff.prev!}
                            operation={diff.prev}
                            type={diff.type === 'modified' ? 'deleted' : diff.type}
                            selectScreenshot={() => {
                              setScreenshotDiff({
                                opened: true,
                                oldOperationId: screenshotOperationIdPairs[idx]?.at(0),
                                newOperationId: screenshotOperationIdPairs[idx]?.at(1),
                              });
                            }}
                          />
                        : <div className="h-full rounded-lg bg-zinc-100/50 dark:bg-zinc-700/30" />}
                      </div>

                      {/* Current Operation */}
                      <div
                        className={clsx('table-cell w-1/2 px-4 py-1 align-top', {
                          'pt-4': idx === 0,
                          'pb-4': idx === operationDiffs.length - 1 && !aiChanges?.length,
                        })}
                      >
                        {diff.new ?
                          <OperationDiff
                            step={stepDiff.prev!}
                            operation={diff.new}
                            type={diff.type === 'modified' ? 'added' : diff.type}
                            selectScreenshot={() => {
                              setScreenshotDiff({
                                opened: true,
                                oldOperationId: screenshotOperationIdPairs[idx]?.at(0),
                                newOperationId: screenshotOperationIdPairs[idx]?.at(1),
                              });
                            }}
                            testRunAttemptId={aiChanges && aiChanges.length > 0 ? undefined : testRunAttemptId} // hide ai action debug button for autoheal steps
                            aiActionLogs={aiActionLogs}
                          />
                        : <div className="h-full rounded-lg bg-zinc-100/50 dark:bg-zinc-700/30" />}
                      </div>
                    </div>
                  ))}

                  {!!aiChanges && aiChanges.length > 0 && (
                    <div className="table-row">
                      {/* Empty cell in left column */}
                      <div className="table-cell w-1/2 border-r border-[var(--mantine-color-default-border)] p-4 align-top" />
                      {/* AI Changes + Step Level Errors in right column */}
                      <div className="table-cell w-1/2 p-4 align-top">
                        {!!aiChanges && aiChanges.length > 0 && (
                          <div
                            className={clsx(
                              'rounded-md text-xs',
                              stepHealSuccess ? 'bg-gray-100 dark:bg-neutral-700' : 'bg-red-50 dark:bg-red-900/20',
                            )}
                          >
                            <div
                              className={clsx(
                                'mb-2 flex flex-row gap-x-2 rounded-t-md px-1 py-1',
                                stepHealSuccess ?
                                  'bg-blue-500/20 text-blue-600 dark:bg-blue-600/20 dark:text-blue-400'
                                : 'bg-red-400/20 text-red-600 dark:bg-red-600/20 dark:text-red-400',
                              )}
                            >
                              <IconSparkles size={14} />
                              AI {stepHealSuccess ? 'successfully fixed' : 'attempted to fix'} {aiChanges.length} issue
                              {aiChanges.length === 1 ? '' : 's'}
                            </div>
                            <div className="flex flex-row gap-x-2 px-2 pb-3">
                              <List
                                spacing="md"
                                size="sm"
                                icon={<IconTool size={14} />}
                              >
                                {aiChanges.map((change, i) => (
                                  <List.Item key={i}>{change.summary}</List.Item>
                                ))}
                              </List>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Collapse>
        </div>
      </div>
    </div>
  );
}
